import { Color } from '../enums/Color.enum';
import { Fit } from '../enums/Fit.enum';
import { LongSize } from '../enums/Size.enum';
import { DataLayerEvent, DataLayerProduct, ProductPayload } from '../models/DataLayer.model';
import { HoneyloveWindow } from '../models/HoneyloveWindow';
import { ShopifyCheckout } from '../models/ShopifyCheckout.model';
import { shopify } from '../shopify';

declare const window: HoneyloveWindow;

export const userDetected = (externalId: string | undefined, email: string): DataLayerEvent => ({
  event: 'UserDetected',
  externalId,
  email,
});

// note: this is used to fetch the externalId from the server
export const pageInitialized = (): DataLayerEvent => ({
  event: 'PageInitialized',
});

export const checkoutStepEncountered = (productSets: ProductPayload[], step: string, option: string): DataLayerEvent => ({
  event: 'CheckoutStepEncountered',
  ecommerce: {
    currencyCode: currencyCode(),
    checkout: {
      actionField: {
        step,
        option,
      },
      products: productSets.map(productPayload),
    },
  },
});

// these are the inverse of the country coefficients which are used to remove duties and taxes from the purchase price
// Note that when Global-e updates these values, we need to update here as well.
const currencyCodeModifiers: {
  [key: string]: number;
} = {
  AUD: 1 / 1.17,
  BRL: 1 / 1.1,
  CAD: 1 / 1.19,
  HKD: 1 / 1.03,
  MXN: 1 / 1.22,
  NZD: 1 / 1.17,
  SGD: 1 / 1.1,
  GBP: 1 / 1.25,
};

export const purchase = (shopifyCheckout: ShopifyCheckout, productSets: ProductPayload[]): DataLayerEvent => {
  const shipping = parseFloat(shopifyCheckout?.shipping_rate?.price ?? '0');
  const tax = parseFloat(shopifyCheckout.total_tax ?? '0');
  const currencyCode = shopifyCheckout?.presentment_currency || 'USD';
  const coefficientModifier = currencyCodeModifiers[currencyCode] ?? 1;
  const revenue = tax + shipping + Math.round(parseFloat(shopifyCheckout.subtotal_price ?? '0') * coefficientModifier * 100) / 100;
  const phone = normalizePhone(shopifyCheckout?.billing_address?.phone);
  return {
    event: 'Purchase',
    email: shopifyCheckout?.email,
    phone,
    firstName: shopifyCheckout?.billing_address?.first_name,
    lastName: shopifyCheckout?.billing_address?.last_name,
    city: shopifyCheckout?.billing_address?.city,
    state: shopifyCheckout?.billing_address?.province_code,
    zip: shopifyCheckout?.billing_address?.zip,
    country: shopifyCheckout?.billing_address?.country_code,
    ecommerce: {
      currencyCode,
      purchase: {
        actionField: {
          id: shopifyCheckout?.order_id,
          affiliation: 'Online Store',
          revenue,
          tax,
          shipping,
          shippingOption: shopifyCheckout?.shipping_rate?.title,
          coupon: shopifyCheckout?.discount?.code,
        },
        products: productSets.map((set) => productPayload(set)),
      },
    },
  };
};

const normalizePhone = (phone: string | undefined) => {
  const normalizedPhone = phone?.replace(/[^\d]+/g, '').replace(/^0+/g, '') ?? '';
  return normalizedPhone.length === 10 ? `1${normalizedPhone}` : normalizedPhone;
};

export const paymentGatewayUsed = (gateway: string): DataLayerEvent => ({
  event: 'PaymentGatewayUsed',
  paymentGateway: gateway,
});

export const userSignedIn = (): DataLayerEvent => ({ event: 'UserSignedIn' });

export const userSignedOut = (): DataLayerEvent => ({ event: 'UserSignedOut' });

export const userRegistered = (): DataLayerEvent => ({ event: 'UserRegistered' });

export const productImageViewed = (productTitle: string): DataLayerEvent => ({
  event: 'ProductImageViewed',
  label: productTitle,
});

export const purchaseRefererTest = (transactionId: string): DataLayerEvent => ({
  event: 'PurchaseRefererTest',
  label: transactionId,
});

export const purchaseTimingTest = (transactionId: string): DataLayerEvent => ({
  event: 'PurchaseTimingTest',
  label: transactionId,
});

export const purchaseDualTest = (transactionId: string): DataLayerEvent => ({
  event: 'purchaseDualTest',
  label: transactionId,
});

export const sizingToolUserResponseEvent = ({ sizingToolUserResponse, handle }: Pick<DataLayerEvent, 'sizingToolUserResponse' | 'handle'>): DataLayerEvent => ({
  event: 'SizingToolResponse',
  sizingToolUserResponse,
  handle,
});

export const quizToolUserResponseEvent = ({ quizToolUserResponse }: Pick<DataLayerEvent, 'quizToolUserResponse'>): DataLayerEvent => ({
  event: 'QuizToolResponse',
  quizToolUserResponse,
});

export const postPurchaseUserResponseEvent = ({ postPurchaseUserResponse }: Pick<DataLayerEvent, 'postPurchaseUserResponse'>): DataLayerEvent => ({
  event: 'PostPurchaseResponse',
  postPurchaseUserResponse,
});

export const experimentViewed = (experimentId: string, variationId: number): DataLayerEvent => ({
  event: 'ExperimentViewed',
  experimentId,
  variationId,
});

// if a variant isn't selected we can still accept color and size by themselves
const productPayload = ({ product, variant, position, color, size, fit }: ProductPayload, quantity?: number): DataLayerProduct => {
  const variantColor = (variant && (variant.color as Color)) || color;
  const variantSize = (variant && (variant.size as LongSize)) || size;
  const variantFit = (variant && (variant.fit as Fit)) || fit;
  const image_url = variant?.featured_image?.src ? variant.featured_image.src : product?.featuredImage && product?.featuredImage.replace('.jpg', '_grande.jpg');

  return removeUndefinedValues({
    name: product?.title,
    id: product?.sku,
    price: product?.price,
    brand: product?.vendor,
    category: product?.type,
    list: product?.list,
    variant: variant && variant.title,
    dimension1: variantFit,
    dimension2: variantColor,
    dimension3: variantSize,
    quantity: quantity || (variant && variant.quantity),
    handle: product?.handle,
    position,
    // non enhanced ecommerce keys here
    tags: product?.tags,
    image_url,
    variant_id: variant && variant.sku,
    shopify_id: product?.id,
    shopify_variant_id: variant && variant.id,
    // this is used for remarketing
    viewed_variant_id: viewedVariantId(product?.sku, variant && variant.sku, variantColor, variantSize),
  });
};

const viewedVariantId = (productSku?: string, variantSku?: string, color?: Color, size?: LongSize, fit?: Fit) => {
  const skuColorMap = (c?: Color) => {
    switch (c) {
      case Color.Astral:
        return 'Eliz'; // Astral old color name was Elizabeth
      case Color.Vamp:
        return 'Cleo'; // Vamp old color name was Cleo
      default:
        return c || '';
    }
  };
  return variantSku || `${productSku}-${skuColorMap(color)}-${size || 'XL'}${fit ? `-${fit}` : ''}`;
};

const pageContext = (pathname: string) =>
  pathname.match('^/products/') ? 'Product Detail Page' : pathname.match('^/cart') ? 'Cart' : pathname.match('/checkouts/') ? 'Checkout' : 'Landing Page';

const currencyCode = (): string =>
  shopify?.Checkout && typeof window !== 'undefined' ? window.Shopify?.Checkout?.currency ?? '' : shopify?.currency.active ?? '';

const removeUndefinedValues = (obj: any) => Object.keys(obj).reduce((acc, key) => (obj[key] !== undefined ? { ...acc, [key]: obj[key] } : acc), {});
